/**
 * VM Table Rendering
 *
 * Uses a schema fetched from /api/vms/schema to drive dynamic table columns
 * and expandable detail rows. Fixed columns (expand toggle, Name, Description,
 * Status, CPUs, Memory, Actions) are always present; schema attributes with
 * location "column" are inserted before Actions.
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import type { VmListItem, VmSchemaAttribute, RdpUser, VmStatus } from '../api/types.js';
import type { WsVmStatus } from '../api/vmstatus-ws.js';
import { escapeHtml, formatDuration } from '../utils.js';
import { vms, setVms } from '../globals.js';
import { generateSplitButton, type DropdownItem } from '../ui/forms.js';
import { hasPermission } from '../app/init.js';

// Track which VMs have their detail rows expanded
const expandedVms = new Set<number>();

// Deferred re-render: when a dropdown is open we skip the render and set this
// flag so the mouseout handler picks it up.
let renderDeferred = false;

// Cached schema (fetched once at init)
let schema: VmSchemaAttribute[] = [];

// Visible optional columns (configurable per-user).
// Name, Description, and Actions are always shown — this set controls everything else.
let visibleColumns: Set<string> = new Set(['cpus', 'memory']);

/**
 * Set which optional columns are visible in the VM table.
 */
export function setVisibleColumns(cols: string[]): void {
  visibleColumns = new Set(cols);
}

// Uptime tick interval handle
let uptimeTimer: ReturnType<typeof setInterval> | null = null;


/**
 * Extract a VM attribute value by schema key.
 * Handles keys that don't map 1:1 to VM fields.
 */
function getVmAttributeValue(vm: VmListItem, key: string): unknown {
  switch (key) {
    case 'mac_address':
      return (vm as unknown as Record<string, unknown>).macaddr;
    case 'ip_address':
      return vm.ip_address;
    case 'rdp_users':
      return vm.rdp_users;
    case 'mapped_ports':
      return vm.mapped_ports;
    default:
      return (vm as unknown as Record<string, unknown>)[key];
  }
}

/**
 * Format a VM attribute value for display based on its schema type.
 */
function formatAttributeValue(value: unknown, type: string): string {
  if (value === undefined || value === null) return '-';

  switch (type) {
    case 'duration':
      return typeof value === 'number' ? formatDuration(value) : '-';

    case 'port_list': {
      const ports = value as Array<{ host_port: number; guest_port: number; protocol: string }>;
      if (!Array.isArray(ports) || ports.length === 0) return '-';
      return escapeHtml(
        ports.map(p => `${p.host_port}:${p.guest_port}/${p.protocol}`).join(', ')
      );
    }

    case 'rdp_users': {
      const users = value as RdpUser[];
      if (!Array.isArray(users) || users.length === 0) return '-';
      return escapeHtml(
        users.map(u => {
          const name = u.identity || '?';
          return u.remote_ip ? `${name} (${u.remote_ip})` : name;
        }).join(', ')
      );
    }

    case 'boolean':
      return value ? 'Yes' : 'No';

    case 'number':
      return String(value);

    case 'string':
    default:
      return escapeHtml(String(value));
  }
}

/**
 * Get the cached VM schema (for use by other modules like settings).
 */
export function getSchema(): VmSchemaAttribute[] {
  return schema;
}

/**
 * Toggleable column keys rendered between the always-present columns.
 * Rendering order: [expand] [name] [description] [OPTIONAL...] [schema...] [actions]
 * Name, Description, and Actions are always present (not toggleable).
 */
const OPTIONAL_COLUMNS = ['cpus', 'memory'] as const;

/**
 * Get schema attributes that are visible in the table columns.
 */
function schemaColumnsVisible(): VmSchemaAttribute[] {
  return schema.filter(a => visibleColumns.has(a.key));
}

/**
 * Get schema attributes that are NOT visible — shown in the detail row.
 */
function schemaColumnsDetail(): VmSchemaAttribute[] {
  return schema.filter(a => !visibleColumns.has(a.key));
}

/**
 * Total number of columns.
 * Always-present: expand + name + description + actions = 4
 * Optional: status, cpus, memory (if in visibleColumns)
 * Schema: visible schema columns
 */
function totalColumns(): number {
  let count = 5; // expand + name + description + status + actions
  for (const col of OPTIONAL_COLUMNS) {
    if (visibleColumns.has(col)) count++;
  }
  return count + schemaColumnsVisible().length;
}

/**
 * Format CPUs column: "usage/total" when running with data, just "total" otherwise.
 */
function formatCpuDisplay(vm: VmListItem): string {
  if (vm.cpu_usage != null && vm.status === 'running') {
    return `${vm.cpu_usage.toFixed(1)}/${vm.cpus}`;
  }
  return String(vm.cpus);
}

/**
 * Format VM status for display
 */
function formatStatus(status: string): { text: string; class: string } {
  switch (status) {
    case 'running':
      return { text: 'Running', class: 'status-running' };
    case 'stopped':
      return { text: 'Stopped', class: 'status-stopped' };
    case 'paused':
      return { text: 'Paused', class: 'status-paused' };
    case 'starting':
      return { text: 'Starting...', class: 'status-starting' };
    case 'stopping':
      return { text: 'Stopping...', class: 'status-stopping' };
    case 'unknown':
      return { text: 'Unknown', class: 'status-unknown' };
    default:
      return { text: status, class: 'status-unknown' };
  }
}

/**
 * Generate action buttons for a VM
 */
function generateVmActions(vm: VmListItem): string {
  const id = vm.id;
  const isRunning = vm.status === 'running';
  const isPaused = vm.status === 'paused';
  const canAdmin = hasPermission('create-vms');
  const svc = new Set(vm.services || []);

  if (isRunning || isPaused) {
    const items: DropdownItem[] = [];

    // Connect services — only show if enabled
    if (svc.has('guest_vnc')) {
      items.push({ icon: '🖥', name: 'Screen View (VNC Guest)', func: `vmConnectGuestVnc(${id})` });
    }
    if (svc.has('vnc_console')) {
      items.push({ icon: '📺', name: 'Console (VNC)', func: `vmConnectVnc(${id})` });
    }

    if (isRunning) {
      items.push(
        { icon: '⏹', name: 'Stop', func: `vmStop(${id})` },
        { icon: '🔄', name: 'Restart', func: `vmRestart(${id})` },
        { divider: true, name: '', func: '' },
        { icon: '⏸', name: 'Pause', func: `vmPause(${id})` },
      );
    } else {
      items.push(
        { icon: '▶', name: 'Resume', func: `vmResume(${id})` },
      );
    }
    items.push(
      { divider: true, name: '', func: '' },
      { icon: '🔀', name: 'Port Forwards', func: `vmPortForwards(${id})` },
      { icon: '⚙', name: 'Monitor', func: `vmOpenMonitor(${id})` },
    );
    if (isRunning && svc.has('serial_console')) {
      items.push({ icon: '⌨', name: 'Console (Serial)', func: `vmOpenSerial(${id})` });
    }
    items.push(
      { icon: '⚠', name: 'Force Off', func: `vmForcePowerOff(${id})`, danger: true },
      { icon: '⚠', name: 'Force Restart', func: `vmForceRestart(${id})`, danger: true },
    );
    if (canAdmin) {
      items.push({ divider: true, name: '', func: '' });
      items.push({ icon: '✎', name: 'Modify', func: `vmModify(${id})` });
    }

    // Primary button: RDP if enabled, otherwise first available connect service
    if (svc.has('rdp')) {
      return generateSplitButton('💻 Connect (RDP)', `vmConnectRdp(${id})`, items);
    } else if (svc.has('guest_vnc')) {
      return generateSplitButton('🖥 Screen View', `vmConnectGuestVnc(${id})`, items);
    } else if (svc.has('vnc_console')) {
      return generateSplitButton('📺 Console (VNC)', `vmConnectVnc(${id})`, items);
    } else {
      return generateSplitButton('Actions', '', items);
    }
  } else {
    // Stopped
    const items: DropdownItem[] = [];
    if (canAdmin) {
      items.push({ icon: '✎', name: 'Modify', func: `vmModify(${id})` });
      items.push({ icon: '🗑', name: 'Delete', func: `vmDelete(${id})`, danger: true });
    }
    return generateSplitButton('Start', `vmStart(${id})`, items);
  }
}

/**
 * Render the thead based on fixed columns + schema-driven dynamic columns.
 */
function renderTableHead(): void {
  const thead = $('#vms thead');
  if (!thead.length) return;

  const columnAttrs = schemaColumnsVisible();
  const dynamicHeaders = columnAttrs.map(a => `<th>${escapeHtml(a.label)}</th>`).join('');

  const vc = visibleColumns;
  thead.html(`
    <tr>
      <th></th>
      <th>Name</th>
      <th>Description</th>
      <th>Status</th>
      ${vc.has('cpus') ? '<th>CPUs</th>' : ''}
      ${vc.has('memory') ? '<th>Memory</th>' : ''}
      ${dynamicHeaders}
      <th>Actions</th>
    </tr>
  `);
}

/**
 * Render the VM table
 */
export function renderVmTable(vmList: VmListItem[]): void {
  const tbody = $('#vm-table-body');
  if (!tbody.length) return;

  // Always sort by VM ID for consistent ordering
  vmList.sort((a, b) => a.id - b.id);

  renderTableHead();
  tbody.empty();

  const cols = totalColumns();

  if (vmList.length === 0) {
    const createLink = hasPermission('create-vms')
      ? ' <a href="#" onclick="vmCreate(); return false;">Create a new VM</a>'
      : '';
    tbody.append(`
      <tr>
        <td colspan="${cols}" class="no-vms">
          No VMs configured.${createLink}
        </td>
      </tr>
    `);
    return;
  }

  const columnAttrs = schemaColumnsVisible();

  const vc = visibleColumns;

  for (const vm of vmList) {
    const status = formatStatus(vm.status);
    let memoryDisplay = vm.mem_gb.toFixed(1) + ' GB';
    if (vm.balloon_actual_mb != null && vm.balloon_actual_mb > 0) {
      const actualMb = vm.balloon_actual_mb;
      const freeMb = vm.balloon_free_mb ?? 0;
      const usedGb = (Math.max(actualMb - freeMb, 0) / 1024).toFixed(1);
      const targetGb = (actualMb / 1024).toFixed(1);
      const allocGb = vm.mem_gb.toFixed(1);
      memoryDisplay = `${usedGb}/${targetGb}/${allocGb} GB`;
    }
    const isExpanded = expandedVms.has(vm.id);
    const toggle = isExpanded ? '\u25BC' : '\u25B6';

    // Dynamic column cells
    const dynamicCells = columnAttrs.map(a => {
      const val = getVmAttributeValue(vm, a.key);
      const durationAttr = a.type === 'duration' ? ` data-vm-duration="${vm.id}" data-vm-duration-key="${a.key}"` : '';
      return `<td class="vm-${a.key}"${durationAttr}>${formatAttributeValue(val, a.type)}</td>`;
    }).join('');

    const row = `
      <tr data-vm-id="${vm.id}">
        <td class="vm-expand-toggle" onclick="toggleVmDetails(${vm.id})">${toggle}</td>
        <td class="vm-name">${vm.id}/${escapeHtml(vm.name)}</td>
        <td class="vm-description">${escapeHtml(vm.description || '')}</td>
        <td class="vm-status"><span class="status-badge ${status.class}">${status.text}</span></td>
        ${vc.has('cpus') ? `<td class="vm-cpus">${formatCpuDisplay(vm)}</td>` : ''}
        ${vc.has('memory') ? `<td class="vm-memory">${memoryDisplay}</td>` : ''}
        ${dynamicCells}
        <td class="vm-actions">${generateVmActions(vm)}</td>
      </tr>
      <tr class="vm-details-row" style="display: ${isExpanded ? '' : 'none'}">
        <td colspan="${cols}">${generateVmDetails(vm)}</td>
      </tr>
    `;
    tbody.append(row);
  }
}

/**
 * Map lifecycle_state from WebSocket to a VmStatus for the table.
 */
function lifecycleToStatus(ws: WsVmStatus): VmStatus {
  switch (ws.lifecycle_state) {
    case 'paused':
    case 'pausing':
      return 'paused';
    case 'running':
    case 'idle':
    case 'resuming':
      return 'running';
    case 'starting':
      return 'starting';
    case 'stopping':
      return 'stopping';
    case 'stopped':
      return 'stopped';
    case 'unknown':
      return 'unknown';
    default:
      return ws.running ? 'running' : 'stopped';
  }
}

// Fields on the WS message that are internal / not VM properties
const WS_SKIP_FIELDS = new Set(['type', 'running', 'id']);

// Lifecycle/runtime fields owned by the execution host (the host running QEMU).
// For delegated VMs, the local (frontend-facing) host should NOT overwrite these —
// they come from the remote host's WebSocket instead.  The local host only provides
// connection-related fields (active_connections, mapped_ports, idle_seconds, etc.).
const REMOTE_OWNED_FIELDS = new Set([
  'lifecycle_state', 'health', 'pid', 'qmp_available',
  'ip_address', 'uptime_seconds', 'balloon_total_mb', 'balloon_free_mb',
  'balloon_actual_mb', 'cpu_usage', 'rdp_users',
]);

/**
 * Update a single VM's status from a WebSocket message.
 * Merges all fields from the WS message into the VM object so that
 * schema-driven columns and detail rows always reflect the latest data.
 */
export function updateVmInTable(status: WsVmStatus): void {
  // Look up by ID first (handles renames), fall back to name
  const existing = (status.id != null && vms.find(vm => vm.id === status.id))
    || vms.find(vm => vm.name === status.name);
  if (!existing) return; // VM not in our list yet — wait for next full refresh

  // Reject updates from remote hosts for VMs not delegated to them.
  // This guards against ID conflicts where a remote host has a different
  // VM with the same ID as a local or differently-delegated VM.
  if (status.host && existing.host !== status.host) return;

  // For delegated VMs, local WS updates (no host tag) should only apply
  // connection-related fields — lifecycle/runtime state comes from the
  // remote host's WS.
  const isLocalUpdateForDelegatedVm = !status.host && !!existing.host;

  // Merge all fields from WS message onto the VM object first,
  // then derive status from the merged state. This is important for
  // diff-based updates where only changed fields are sent.
  const src = status as unknown as Record<string, unknown>;
  const dst = existing as unknown as Record<string, unknown>;
  for (const key of Object.keys(src)) {
    if (WS_SKIP_FIELDS.has(key)) continue;
    if (isLocalUpdateForDelegatedVm && REMOTE_OWNED_FIELDS.has(key)) continue;
    if (src[key] === null) {
      delete dst[key];  // Field was removed (diff sends null for deletions)
    } else if (src[key] !== undefined) {
      dst[key] = src[key];
    }
  }

  // Derive table status from the merged VM state (not the partial diff).
  // Build a synthetic WsVmStatus from the merged VM so lifecycleToStatus
  // sees the full picture, not just the changed fields.
  // Also re-derive when the VM is currently 'unknown' — any WS message
  // arriving means the backend is monitoring this VM, so we should
  // transition out of 'unknown' even if the diff doesn't include
  // lifecycle_state/running (e.g. a stopped VM with only metadata).
  if (!isLocalUpdateForDelegatedVm &&
      ('lifecycle_state' in src || 'running' in src || existing.status === 'unknown')) {
    existing.status = lifecycleToStatus({
      type: 'vm_status',
      name: existing.name,
      running: dst['running'] as boolean,
      lifecycle_state: dst['lifecycle_state'] as WsVmStatus['lifecycle_state'],
    });
  }

  // Defer re-render if a dropdown menu is currently open — a full DOM rebuild
  // while the user is hovering a menu destroys it mid-click.
  if ($('.split-btn:hover').length > 0) {
    renderDeferred = true;
    return;
  }

  renderVmTable(vms);
}

/**
 * Generate the property/value sub-table for a VM's detail row,
 * driven by schema attributes with location "detail" or "both".
 */
function generateVmDetails(vm: VmListItem): string {
  const detailAttrs = schemaColumnsDetail();

  if (detailAttrs.length === 0) {
    return '<em>No details available</em>';
  }

  const rows = detailAttrs.map(a => {
    const val = getVmAttributeValue(vm, a.key);
    const durationAttr = a.type === 'duration' ? ` data-vm-duration="${vm.id}" data-vm-duration-key="${a.key}"` : '';
    return `<tr><td><strong>${escapeHtml(a.label)}</strong></td><td${durationAttr}>${formatAttributeValue(val, a.type)}</td></tr>`;
  }).join('');

  return `<table class="vm-details-table">${rows}</table>`;
}

/**
 * Toggle a VM's detail row visibility
 */
export function toggleVmDetails(vmId: number): void {
  if (expandedVms.has(vmId)) {
    expandedVms.delete(vmId);
  } else {
    expandedVms.add(vmId);
  }
  renderVmTable(vms);
}

/**
 * Tick duration fields (uptime, idle time) for all running VMs by 1 second
 * and update the DOM directly. Only touches elements tagged with
 * data-vm-duration="<id>" data-vm-duration-key="<key>".
 */
function tickDurations(): void {
  for (const vm of vms) {
    if (vm.status !== 'running') continue;
    const vmAny = vm as unknown as Record<string, unknown>;
    $(`[data-vm-duration="${vm.id}"]`).each(function () {
      const key = $(this).attr('data-vm-duration-key');
      if (!key) return;
      const cur = vmAny[key];
      if (typeof cur !== 'number') return;
      vmAny[key] = cur + 1;
      $(this).text(formatDuration(cur + 1));
    });
  }
}

/**
 * Start the 1-second duration tick interval.
 */
function startUptimeTimer(): void {
  stopUptimeTimer();
  uptimeTimer = setInterval(tickDurations, 1000);
}

/**
 * Stop the uptime tick interval.
 */
export function stopUptimeTimer(): void {
  if (uptimeTimer !== null) {
    clearInterval(uptimeTimer);
    uptimeTimer = null;
  }
}

/**
 * Set up delegated mouseenter/mouseleave on split-btn dropdowns:
 * - Flip direction when the menu would overflow the viewport bottom.
 * - Flush deferred re-renders when the mouse leaves the dropdown area.
 */
function setupDropdownFlip(): void {
  $(document).on('mouseenter', '.split-btn', function () {
    const btn = $(this);
    const menu = btn.find('.dropdown-menu');
    if (!menu.length) return;

    // Temporarily show to measure height, then hide
    menu.css('visibility', 'hidden').css('display', 'block');
    const menuHeight = menu.outerHeight() || 0;
    menu.css('display', '').css('visibility', '');

    const btnBottom = this.getBoundingClientRect().bottom;
    const spaceBelow = window.innerHeight - btnBottom;

    btn.toggleClass('dropup', spaceBelow < menuHeight + 10);
  });

  $(document).on('mouseleave', '.split-btn', function () {
    if (renderDeferred) {
      renderDeferred = false;
      renderVmTable(vms);
    }
  });
}

/**
 * Initialize the VM table
 */
export async function initVmTable(): Promise<void> {
  // Fetch schema and VM list in parallel
  const [schemaResult, vmList] = await Promise.all([
    apiCall(api.getVmsSchema(), 'Failed to load VM schema'),
    apiCall(api.listVms(), 'Failed to Load VMs'),
  ]);

  if (schemaResult) {
    schema = schemaResult;
  }

  if (vmList) {
    setVms(vmList);
    renderVmTable(vmList);
  }

  startUptimeTimer();
  setupDropdownFlip();
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).toggleVmDetails = toggleVmDetails;
}
